// src/Dashboard/pages/user/Reglements/hooks/useUserReglements.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";
import formatDate from "../../../../../utils/formatDate";

import { getSettings } from "../../../../../services/settingsServices";
import { getReglementsByUser } from "../../../../../services/achatsServices";
import { createCheckoutSessionForArticles } from "../../../../../services/paymentServices";
import { sendNotificationToAdmin } from "../../../../../services/notificationServices";
import { generateReglementInvoicePdf } from "../utils/invoicePdfUtils";

import {
  DEFAULT_REGLEMENTS_UI,
  NOTIFIED_STORAGE_KEY,
  PAGE_SIZE,
  PAID_STATUSES,
  PAYABLE_STATUSES,
  REGLEMENTS_UI_STORAGE_KEY,
  buildReglementSearchText,
  canPayReglement,
  canPayInInstallments,
  computeUnitPrice,
  getArticleName,
  isRecent,
  prepareReglement,
  readStorageJson,
  toNumberSafe,
  writeStorageJson,
  collatorFR,
  canGenerateInvoice,
} from "../utils/reglementsUtils";

export function useUserReglements() {
  const { user } = useUser();

  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [defaultPhotoUrl, setDefaultPhotoUrl] = useState("");
  const [maxPayments, setMaxPayments] = useState(5);
  const [paymentInterval, setPaymentInterval] = useState(1);

  const [sessionId, setSessionId] = useState(null);

  const [payingId, setPayingId] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);
  const [manualNotifiedIds, setManualNotifiedIds] = useState([]);

  const [paymentPlanOpen, setPaymentPlanOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const notifiedIdsRef = useRef(new Set());
  const prevStatusRef = useRef(new Map());
  const autoNotifyNextLoadRef = useRef(false);
  const autoNotifyLockRef = useRef(false);

  const [settingsInfo, setSettingsInfo] = useState(null);

  const [ui, setUi] = useState(() => {
    const saved = readStorageJson(REGLEMENTS_UI_STORAGE_KEY, null);
    return saved
      ? { ...DEFAULT_REGLEMENTS_UI, ...saved }
      : DEFAULT_REGLEMENTS_UI;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const persistUi = useCallback((next) => {
    setUi(next);
    writeStorageJson(REGLEMENTS_UI_STORAGE_KEY, next);
  }, []);

  const updateUi = useCallback(
    (patch) => {
      const shouldResetPage = [
        "search",
        "status",
        "season",
        "focus",
        "sort",
      ].some((key) => Object.prototype.hasOwnProperty.call(patch, key));

      persistUi({
        ...ui,
        ...patch,
        perPage: shouldResetPage ? PAGE_SIZE : (patch.perPage ?? ui.perPage),
      });
    },
    [persistUi, ui],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      updateUi({
        search: debouncedSearch,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedSearch]); // volontairement limité

  useEffect(() => {
    const saved = readStorageJson(NOTIFIED_STORAGE_KEY, []);
    if (Array.isArray(saved)) {
      notifiedIdsRef.current = new Set(saved);
    }
  }, []);

  const persistNotified = useCallback(() => {
    writeStorageJson(NOTIFIED_STORAGE_KEY, Array.from(notifiedIdsRef.current));
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();

        if (Array.isArray(settings) && settings.length > 0) {
          const s = settings[0];

          setSettingsInfo(s);
          setDefaultPhotoUrl(s.photo_url_default_lic_cot || "");
          setMaxPayments(s.max_payments ?? 5);
          setPaymentInterval(s.payment_interval ?? 1);
        }
      } catch (error) {
        console.error("Erreur récupération paramètres règlements :", error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sid = query.get("session_id");

    if (sid) {
      setSessionId(sid);
      autoNotifyNextLoadRef.current = true;
    }
  }, []);

  const handleGenerateInvoice = useCallback(
  (reglementOrId) => {
    const reglement =
      typeof reglementOrId === "object"
        ? reglementOrId
        : reglements.find((item) => item.id === reglementOrId);

    if (!reglement) {
      toast.error("Impossible de trouver le règlement.");
      return;
    }

    if (!canGenerateInvoice(reglement)) {
      toast.info(
        "La facture est disponible uniquement pour les articles boutique réglés."
      );
      return;
    }

    try {
      generateReglementInvoicePdf({
        reglement,
        user,
        settings: settingsInfo,
      });

      toast.success("Facture PDF générée.");
    } catch (error) {
      console.error("Erreur génération facture PDF :", error);
      toast.error("Impossible de générer la facture PDF.");
    }
  },
  [reglements, settingsInfo, user]
);

  const checkAutoNotifications = useCallback(
    async (freshList) => {
      if (autoNotifyLockRef.current) return;

      if (!autoNotifyNextLoadRef.current) {
        freshList.forEach((reglement) => {
          prevStatusRef.current.set(reglement.id, reglement.statut_reglement);
        });
        return;
      }

      autoNotifyLockRef.current = true;

      try {
        const paid = freshList.filter(
          (reglement) =>
            reglement?.statut_reglement === "Réglé" &&
            !notifiedIdsRef.current.has(reglement.id),
        );

        if (paid.length === 0) return;

        let candidate =
          paid.find(
            (reglement) =>
              reglement.stripe_session_id &&
              reglement.stripe_session_id === sessionId,
          ) ||
          paid.find(
            (reglement) =>
              reglement.session_id && reglement.session_id === sessionId,
          );

        if (!candidate) {
          const recentPaid = paid
            .filter((reglement) =>
              isRecent(reglement.updatedAt || reglement.date_achat, 15),
            )
            .sort(
              (a, b) =>
                new Date(b.updatedAt || b.date_achat) -
                new Date(a.updatedAt || a.date_achat),
            );

          candidate = recentPaid[0];
        }

        if (!candidate) {
          candidate = [...paid].sort(
            (a, b) =>
              new Date(b.updatedAt || b.date_achat) -
              new Date(a.updatedAt || a.date_achat),
          )[0];
        }

        if (candidate) {
          const nomArticle = getArticleName(candidate);

          const message = `${user?.prenom || ""} ${
            user?.nom || ""
          } a effectué un paiement en ligne : ${nomArticle} – ${
            candidate.prix_total
          } € (achat du ${formatDate(candidate.date_achat)}).`;

          await sendNotificationToAdmin(message, user?.token);

          notifiedIdsRef.current.add(candidate.id);
          persistNotified();

          toast.success("Notification envoyée aux administrateurs.");
        }
      } catch (error) {
        console.error("Erreur notification automatique règlement :", error);
        toast.error("Erreur lors de l'envoi de la notification.");
      } finally {
        freshList.forEach((reglement) => {
          prevStatusRef.current.set(reglement.id, reglement.statut_reglement);
        });

        autoNotifyNextLoadRef.current = false;
        autoNotifyLockRef.current = false;
      }
    },
    [persistNotified, sessionId, user?.nom, user?.prenom, user?.token],
  );

  const loadReglements = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const response = await getReglementsByUser(userId);

      if (!Array.isArray(response)) {
        setReglements([]);
        return;
      }

      const sorted = [...response].sort(
        (a, b) => new Date(b.date_achat) - new Date(a.date_achat),
      );

      const prepared = sorted.map((reglement) =>
        prepareReglement(reglement, defaultPhotoUrl),
      );

      await checkAutoNotifications(prepared);

      setReglements(prepared);
    } catch (error) {
      console.error("Erreur récupération règlements :", error);
      toast.error("Impossible de charger vos règlements.");
      setReglements([]);
    } finally {
      setLoading(false);
    }
  }, [userId, defaultPhotoUrl, checkAutoNotifications]);

  useEffect(() => {
    loadReglements();
  }, [loadReglements]);

  useEffect(() => {
    const refreshAfterSuccess = async () => {
      if (!sessionId) return;

      try {
        await loadReglements();
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      }
    };

    refreshAfterSuccess();
  }, [sessionId, loadReglements]);

  const seasons = useMemo(() => {
    return Array.from(
      new Set(reglements.map((reglement) => reglement.saison).filter(Boolean)),
    ).sort((a, b) => collatorFR.compare(b, a));
  }, [reglements]);

  const filteredReglements = useMemo(() => {
    const q = String(ui.search || "")
      .trim()
      .toLowerCase();

    let list = [...reglements];

    if (q) {
      list = list.filter((reglement) =>
        buildReglementSearchText(reglement).includes(q),
      );
    }

    if (ui.status !== "all") {
      list = list.filter(
        (reglement) => reglement.statut_reglement === ui.status,
      );
    }

    if (ui.season !== "all") {
      list = list.filter((reglement) => reglement.saison === ui.season);
    }

    if (ui.focus === "toPay") {
      list = list.filter((reglement) =>
        PAYABLE_STATUSES.includes(reglement.statut_reglement),
      );
    }

    if (ui.focus === "paid") {
      list = list.filter((reglement) =>
        PAID_STATUSES.includes(reglement.statut_reglement),
      );
    }

    if (ui.focus === "installments") {
      list = list.filter((reglement) =>
        String(reglement.message_achat || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .includes("echeance"),
      );
    }

    list.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(getArticleName(a), getArticleName(b));
      }

      if (ui.sort === "amountDesc") {
        return toNumberSafe(b.prix_total) - toNumberSafe(a.prix_total);
      }

      if (ui.sort === "amountAsc") {
        return toNumberSafe(a.prix_total) - toNumberSafe(b.prix_total);
      }

      const da = new Date(a.date_achat || 0).getTime();
      const db = new Date(b.date_achat || 0).getTime();

      return ui.sort === "dateAsc" ? da - db : db - da;
    });

    return list;
  }, [reglements, ui]);

  const visibleReglements = useMemo(() => {
    return filteredReglements.slice(0, ui.perPage || PAGE_SIZE);
  }, [filteredReglements, ui.perPage]);

  const stats = useMemo(() => {
    const total = reglements.length;

    const toPay = reglements.filter((reglement) =>
      PAYABLE_STATUSES.includes(reglement.statut_reglement),
    );

    const paid = reglements.filter((reglement) =>
      PAID_STATUSES.includes(reglement.statut_reglement),
    );

    const installments = reglements.filter((reglement) =>
      String(reglement.message_achat || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .includes("echeance"),
    );

    const amountDue = toPay.reduce(
      (sum, reglement) => sum + toNumberSafe(reglement.prix_total),
      0,
    );

    const amountPaid = paid.reduce(
      (sum, reglement) => sum + toNumberSafe(reglement.prix_total),
      0,
    );

    const totalAmount = reglements.reduce(
      (sum, reglement) => sum + toNumberSafe(reglement.prix_total),
      0,
    );

    return {
      total,
      filtered: filteredReglements.length,
      toPay: toPay.length,
      paid: paid.length,
      installments: installments.length,
      amountDue,
      amountPaid,
      totalAmount,
    };
  }, [reglements, filteredReglements.length]);

  const resetFilters = useCallback(() => {
    setDebouncedSearch("");
    persistUi(DEFAULT_REGLEMENTS_UI);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    persistUi({
      ...ui,
      perPage: (ui.perPage || PAGE_SIZE) + PAGE_SIZE,
    });
  }, [persistUi, ui]);

  const handlePayerClick = useCallback(
    async (reglementId) => {
      if (payingId) return;

      try {
        const reglement = reglements.find((item) => item.id === reglementId);

        if (!reglement) return;
        if (!canPayReglement(reglement)) return;

        setPayingId(reglementId);

        const quantity = Math.max(1, Number(reglement.quantite) || 1);
        const unitPrice = computeUnitPrice(reglement);

        const items = [
          {
            id: reglement.id,
            name: getArticleName(reglement),
            price: unitPrice,
            quantity,
          },
        ];

        const expectedTotal = Number((unitPrice * quantity).toFixed(2));
        const frontTotal = Number(
          (Number(reglement.prix_total) || 0).toFixed(2),
        );

        if (Math.abs(expectedTotal - frontTotal) > 0.02) {
          console.warn(
            "[Paiement] Total recalculé différent du total affiché :",
            {
              expectedTotal,
              frontTotal,
              reglement,
            },
          );
        }

        const url = await createCheckoutSessionForArticles(items);

        if (typeof url === "string" && url.startsWith("http")) {
          window.location.href = url;
        } else {
          toast.error("URL de paiement invalide.");
          console.error("URL de paiement invalide :", url);
        }
      } catch (error) {
        console.error("Erreur création session paiement :", error);
        toast.error("Impossible de lancer le paiement.");
      } finally {
        setPayingId(null);
      }
    },
    [payingId, reglements],
  );

  const openPaymentPlan = useCallback(
    (reglementId) => {
      const article = reglements.find((item) => item.id === reglementId);

      if (!article) return;
      if (!canPayInInstallments(article)) return;

      setSelectedArticle(article);
      setPaymentPlanOpen(true);
    },
    [reglements],
  );

  const closePaymentPlan = useCallback(() => {
    setPaymentPlanOpen(false);
    setSelectedArticle(null);
  }, []);

  const handlePaymentPlanSaved = useCallback(async () => {
    await loadReglements();
    closePaymentPlan();
  }, [loadReglements, closePaymentPlan]);

  const handleNotifyAdmins = useCallback(
    async (article) => {
      if (!article || notifyingId) return;

      if (
        notifiedIdsRef.current.has(article.id) ||
        manualNotifiedIds.includes(article.id)
      ) {
        toast.info("Cette notification a déjà été envoyée.");
        return;
      }

      try {
        setNotifyingId(article.id);

        const nomArticle = getArticleName(article);

        const message = `${user?.prenom || ""} ${
          user?.nom || ""
        } a confirmé son paiement en ligne : ${nomArticle} – ${
          article.prix_total
        } € (achat du ${formatDate(article.date_achat)}).`;

        await sendNotificationToAdmin(message, user?.token);

        notifiedIdsRef.current.add(article.id);
        persistNotified();

        setManualNotifiedIds((ids) => [...new Set([...ids, article.id])]);

        toast.success("Notification envoyée aux administrateurs.");
      } catch (error) {
        console.error("Erreur notification admin :", error);
        toast.error("Erreur lors de l'envoi de la notification.");
      } finally {
        setNotifyingId(null);
      }
    },
    [
      manualNotifiedIds,
      notifyingId,
      persistNotified,
      user?.nom,
      user?.prenom,
      user?.token,
    ],
  );

  const isAlreadyNotified = useCallback(
    (id) => {
      return notifiedIdsRef.current.has(id) || manualNotifiedIds.includes(id);
    },
    [manualNotifiedIds],
  );

  return {
    user,
    userRole,
    userId,

    reglements,
    filteredReglements,
    visibleReglements,
    seasons,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    defaultPhotoUrl,
    maxPayments,
    paymentInterval,

    sessionId,

    payingId,
    notifyingId,
    isAlreadyNotified,

    selectedArticle,
    paymentPlanOpen,
    openPaymentPlan,
    closePaymentPlan,
    handlePaymentPlanSaved,

    handlePayerClick,
    handleNotifyAdmins,

    refresh: loadReglements,
    handleGenerateInvoice,
  };
}
